import { Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AuthenticatedUser } from '@/auth/types';
import { NetworkStatusBadge } from '@/components/network/NetworkStatusBadge';
import { Button } from '@/components/ui/button';
import { setLocale, SUPPORTED_LOCALES, type SupportedLocale } from '@/i18n';
import { cn } from '@/lib/utils';
import { UserMenu } from './UserMenu';

interface TopbarProps {
  user: AuthenticatedUser;
  /** Mobile: clicking the burger toggles the sidebar drawer. */
  onOpenSidebar: () => void;
}

/**
 * Topbar — sticky header showing (left to right):
 *  1. Mobile-only burger button to open the sidebar drawer.
 *  2. Inline language switcher with three pill buttons (FR / EN / ES).
 *  3. User menu (avatar + dropdown with logout).
 *
 * The brand title is rendered inside the Sidebar to avoid duplication
 * when both are visible on desktop.
 */
export function Topbar({ user, onOpenSidebar }: TopbarProps) {
  const { t, i18n } = useTranslation();

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b bg-background px-4">

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={onOpenSidebar}
          aria-label={t('common.openMenu')}
        >
          <Menu aria-hidden />
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <NetworkStatusBadge />
        <div
          className="flex rounded-md border bg-card p-1"
          role="group"
          aria-label={t('common.language')}
        >
          {SUPPORTED_LOCALES.map((loc: SupportedLocale) => (
            <button
              key={loc}
              type="button"
              onClick={() => setLocale(loc)}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium uppercase transition-colors',
                i18n.language === loc
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-secondary',
              )}
              aria-pressed={i18n.language === loc}
            >
              {loc}
            </button>
          ))}
        </div>
        <UserMenu user={user} />
      </div>
    </header>
  );
}
